import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Globe, Search, ChevronRight, Loader2, Phone, Smartphone } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useCountryRates, CountryRate } from "@/hooks/useCountryRates";
import { NativeFlag } from "@/components/connect/NativeFlag";
import { getFlag } from "@/lib/country-flags";
import { useTranslation } from "react-i18next";


const ROTATING_DESTINATIONS = [
  { name: "Ethiopia", emoji: "🇪🇹", color: "text-green-600" },
  { name: "Nigeria", emoji: "🇳🇬", color: "text-emerald-600" },
  { name: "South Sudan", emoji: "🇸🇸", color: "text-blue-600" },
  { name: "Kenya", emoji: "🇰🇪", color: "text-destructive" },
  { name: "Uganda", emoji: "🇺🇬", color: "text-yellow-600" },
  { name: "Somalia", emoji: "🇸🇴", color: "text-sky-600" },
];

interface HeroCountrySearchProps {
  className?: string;
}

export function HeroCountrySearch({ className }: HeroCountrySearchProps) {
  const { t } = useTranslation("portal");
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<CountryRate | null>(null);
  // Pick one destination per page load and keep it. Auto-rotating every 2.5s
  // reads as flicker, especially on slow first paint where the swap fires
  // before the user has finished reading the placeholder.
  const [destinationIndex] = useState(() =>
    Math.floor(Math.random() * ROTATING_DESTINATIONS.length)
  );
  const navigate = useNavigate();

  const { popular, searchCountries, isLoading, formatRate } = useCountryRates();


  // Filter countries based on query - use live data from database
  const filteredCountries = useMemo(() => {
    if (!query.trim()) {
      // Show popular African destinations when no query
      return popular.slice(0, 8);
    }
    return searchCountries(query).slice(0, 8);
  }, [query, popular, searchCountries]);

  // Helper to get country slug from dial code
  const getCountrySlugFromDialCode = (dialCode: string): string | null => {
    const dialCodeToSlug: Record<string, string> = {
      '211': 'south-sudan',
      '251': 'ethiopia',
      '234': 'nigeria',
      '254': 'kenya',
      '256': 'uganda',
      '252': 'somalia'
    };
    return dialCodeToSlug[dialCode] || null;
  };

  const handleSelect = (country: CountryRate) => {
    const slug = getCountrySlugFromDialCode(country.countryCode);
    if (slug) {
      navigate(`/connect-portal/${slug}`);
    } else {
      // Fallback to rates page for non-supported countries
      setSelectedCountry(country);
      setQuery(country.countryName);
      setIsOpen(false);
    }
  };

  const handleViewRates = () => {
    if (selectedCountry) {
      const slug = getCountrySlugFromDialCode(selectedCountry.countryCode);
      if (slug) {
        navigate(`/connect-portal/${slug}`);
      } else {
        navigate(`/connect-portal/rates?country=${selectedCountry.countryCode}`);
      }
    } else {
      navigate("/connect-portal/rates");
    }
  };

  

  // Get flag for a country - uses database flag or derives from dial code/name
  const getCountryFlag = (country: CountryRate) => {
    return getFlag({
      dialCode: country.countryCode,
      countryName: country.countryName,
      flagEmoji: country.flag,
    });
  };

  const isMobileRate = (country: CountryRate) => country.destinationType === "mobile";

  return (
    <div className={`w-full max-w-xl ${className}`}>
      <div className="relative">
        {/* Main Search Input */}
        <div className="relative">
          <Globe className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 h-5 w-5 sm:h-6 sm:w-6 text-primary z-10" />
          <Input
            type="text"
            placeholder=""
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setIsOpen(true);
              setSelectedCountry(null);
            }}
            onFocus={() => setIsOpen(true)}
            className="h-14 sm:h-[4.5rem] pl-11 sm:pl-14 pr-20 sm:pr-32 text-base sm:text-xl rounded-2xl shadow-lg border-2 border-border focus:border-primary relative z-10 bg-transparent"
          />
          {/* Static placeholder overlay - one destination per page load */}
          {!query && (
            <div className="absolute left-11 sm:left-14 top-1/2 -translate-y-1/2 pointer-events-none flex items-center gap-1.5 overflow-hidden z-0">
              <span className="text-base sm:text-xl text-muted-foreground whitespace-nowrap">Call</span>
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                <NativeFlag
                  emoji={ROTATING_DESTINATIONS[destinationIndex].emoji}
                  className="h-6 w-6 text-2xl sm:h-7 sm:w-8 sm:text-3xl"
                />
                <span className={`text-base sm:text-xl font-semibold ${ROTATING_DESTINATIONS[destinationIndex].color}`}>
                  {ROTATING_DESTINATIONS[destinationIndex].name}
                </span>
              </span>

            </div>
          )}
          <button
            onClick={handleViewRates}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-10 sm:h-14 px-3 sm:px-5 bg-primary text-primary-foreground rounded-xl font-medium flex items-center gap-2 hover:bg-primary/90 transition-colors z-10"
          >
            <Search className="h-4 w-4 sm:h-5 sm:w-5" />
            <span className="hidden sm:inline">Search</span>
          </button>
        </div>

        {/* Dropdown Results */}
        {isOpen && query && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-background border-2 border-border rounded-2xl shadow-xl overflow-hidden z-50">
            {isLoading ? (
              <div className="px-5 py-4 flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading rates...
              </div>
            ) : filteredCountries.length > 0 ? (
              <ul className="max-h-64 overflow-y-auto">
                {filteredCountries.map((country) => (
                  <li key={country.id}>
                    <button
                      onClick={() => handleSelect(country)}
                      className="w-full flex items-center justify-between px-5 py-4 hover:bg-muted transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <NativeFlag
                          emoji={getCountryFlag(country)}
                          label={country.countryName}
                          className="h-8 w-8 text-2xl"
                        />
                        <div className="min-w-0 text-left">
                          <span className="block font-medium truncate">{country.countryName}</span>
                          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                            {isMobileRate(country) ? <Smartphone className="h-3 w-3" /> : <Phone className="h-3 w-3" />}
                            {isMobileRate(country) ? t("mobileRate") : t("landlineRate")}
                          </span>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <span className="text-primary font-bold">
                          {formatRate(country.ratePerMinuteCents)}/min
                        </span>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-5 py-4 text-muted-foreground">
                No countries found
              </div>
            )}
          </div>
        )}

        {/* Selected Country Rate Display */}
        {selectedCountry && !isOpen && (
          <div className="mt-4 text-center flex items-center justify-center gap-3">
            <NativeFlag
              emoji={getCountryFlag(selectedCountry)}
              label={selectedCountry.countryName}
              className="h-10 w-10 text-4xl"
            />
            <span className="text-3xl font-bold">
              {selectedCountry.countryName} - <span className="text-primary">{formatRate(selectedCountry.ratePerMinuteCents)}/min</span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
